import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineArrowBack from '~icons/ic/baseline-arrow-back'
import IcBaselineArrowForward from '~icons/ic/baseline-arrow-forward'
import { UIButton } from 'src/components/ui/Button'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import {
  type ModelExperimentChoice,
  useModelExperimentSession,
  useSubmitModelExperimentPreference,
} from 'src/services/orpc/modelExperiment'
import { getIsMobile } from 'src/utils/hooks/useBreakpoint'

type DialogProps = { taskId: string; close: () => void }

type NoticeKind = 'already-resolved' | 'expired'

// =============================================================================
// Choice dialog — the interactive A/B selection UI shown while
// `session.status === 'selecting'`.
//
// The notice dialog ("this choice was already made on another device" /
// "session expired") is reserved for the case where the user actively
// clicks a choice button and the server rejects it with 409. We do NOT
// pop the notice from a passive status-watch effect — too many false
// positives during the local submit's own React commit ordering, and
// product wants users who never engaged to be left alone.
// =============================================================================

// Module-level dedup: a TaskAndFav card click and the Stage's
// `useExperimentAutoOpenDialog` both call `openModelExperimentDialog`
// for the same root task — the click switches the Stage to that task,
// which mounts a GeneratedItem whose auto-open hook fires on the first
// commit where `session.status === 'selecting'`. Without dedup the user
// sees two stacked dialogs.
const openedTaskIds = new Set<string>()

// Mobile gets a single dismissable "open on desktop" notice instead of
// the comparison UI. Tracked separately so back-to-back trigger sources
// (card click, auto-open hook) don't stack the same fallback either.
let mobileFallbackOpened = false

/**
 * For tests only — clears the dedup sets so the entry-point routing
 * tests aren't sensitive to call order. Not used in production code.
 */
export function _resetDialogDedupForTests() {
  openedTaskIds.clear()
  mobileFallbackOpened = false
}

export function openModelExperimentDialog(taskId: string) {
  if (getIsMobile()) {
    return openMobileFallbackDialog()
  }

  if (openedTaskIds.has(taskId)) return undefined
  openedTaskIds.add(taskId)

  return UIDialog.create({
    dismissable: false,
    type: 'dialog',
    // Override the default `max-w-[400px]` from UIDialog.create so the
    // comparison modal can be wide enough to show two images side by side.
    // `w-auto` makes the dialog hug the inner content's natural width,
    // bounded by the inner content's own `max-w-[90vw]` so it never
    // overflows the viewport on narrow screens.
    className: 'max-w-none w-auto',
    children: ({ close }: { close: () => void }) => (
      <ModelExperimentDialogContent
        taskId={taskId}
        close={() => {
          openedTaskIds.delete(taskId)
          close()
        }}
      />
    ),
  })
}

function openMobileFallbackDialog() {
  if (mobileFallbackOpened) return undefined
  mobileFallbackOpened = true

  return UIDialog.create({
    // Dismissable on mobile: the user can't act on the experiment here
    // anyway, so let them dismiss the notice however they want (tap
    // outside, hardware back on Android, etc).
    dismissable: true,
    type: 'dialog',
    // Backdrop tap / Android hardware-back goes through UIDialog's own
    // onClose path, which does NOT invoke the inner `close` callback
    // below (children's close is wired via `CloseableChildren` and just
    // toggles `setOpen(false)`). Without resetting the dedup flag here
    // it would stay `true` forever after a backdrop dismiss, and every
    // subsequent `openModelExperimentDialog(...)` on mobile would
    // silently no-op until a full page reload. Keep the inner reset
    // too — the "Got it" button path doesn't hit this onClose.
    onClose: () => {
      mobileFallbackOpened = false
    },
    children: ({ close }: { close: () => void }) => (
      <MobileFallbackContent
        close={() => {
          mobileFallbackOpened = false
          close()
        }}
      />
    ),
  })
}

export function ModelExperimentDialogContent({ taskId, close }: DialogProps) {
  const { t } = useTranslation(['generation'])
  const {
    data: session,
    isLoading,
    isError,
  } = useModelExperimentSession({ taskId })
  const submit = useSubmitModelExperimentPreference({ taskId })

  // Latch so close fires at most once even if session.status flips
  // multiple times (e.g. resolved → expired) or `isError` toggles after
  // a retry boundary.
  const handledRef = useRef(false)

  useEffect(() => {
    if (handledRef.current) return
    // Error path: the query exhausted its retries (404, or persistent
    // 5xx). The dialog is `dismissable: false`, so leaving the user on
    // the skeleton with no recovery path would trap them. Close
    // silently — the card click re-runs `readExperimentSessionFromCache`
    // and can re-trigger the dialog if a later refetch succeeds.
    if (isError) {
      handledRef.current = true
      close()
      return
    }
    if (!session) return
    if (session.status === 'selecting') return
    handledRef.current = true
    // Silent close only. We never open the "already chosen / expired"
    // notice from this passive effect — that's reserved for the user's
    // own submit getting rejected with 409 (see `onChoose`).
    close()
  }, [session?.status, isError, close])

  if (isLoading || !session || session.status !== 'selecting') {
    return <DialogSkeleton />
  }

  const leftMedia = session.leftMedias?.[0]
  const rightMedia = session.rightMedias?.[0]
  if (!leftMedia || !rightMedia) return <DialogSkeleton />

  const onChoose = async (choice: ModelExperimentChoice) => {
    try {
      await submit.mutateAsync({ taskId, choice })
      handledRef.current = true
      close()
    } catch (err) {
      const errStatus = (err as { status?: number }).status
      if (errStatus === 409) {
        // The user actively tried to submit and the server says somebody
        // else got there first (or the session expired). This is the only
        // path that surfaces the notice — close *this* dialog and hand
        // off. Pick the notice kind from the server's message hint
        // (`session_expired` / `already_resolved` / `not_yet_selectable`,
        // set in the handler's exhaustive switch), defaulting to
        // "already-resolved" which is the dominant case.
        handledRef.current = true
        close()
        const message = (err as { data?: { message?: string } }).data?.message
        const kind: NoticeKind =
          message === 'session_expired' ? 'expired' : 'already-resolved'
        openModelExperimentNoticeDialog(kind)
      } else {
        toast.error(
          t(
            'generation:model-experiment.dialog.submit-error',
            'Submission failed. Please try again.',
          ),
        )
      }
    }
  }

  // Desktop-only layout. Mobile entry is routed to MobileFallbackContent
  // at the `openModelExperimentDialog` level, so we don't carry a
  // runtime mobile branch here. If the user resizes a desktop dialog
  // below the mmd breakpoint, this still renders (cramped) — acceptable
  // since the dialog is already open and dismissable=false.
  //
  // 55vh tall, but capped at 90vw wide so we never overflow the viewport.
  // ImageSlot is `flex-1 min-w-0 max-h-full aspect-square`, so it sizes
  // from `min(remaining row height, half row width)`.
  return (
    <div className="flex flex-col relative bg-white rounded-lg p-8 gap-6 h-[55vh] max-w-[90vw]">
      <UIButton
        variant="ghost"
        size="small"
        className="absolute right-4 top-4 px-2 text-neutral-500 hover:text-neutral-800"
        onPress={() => onChoose('skip')}
      >
        {t('generation:model-experiment.dialog.skip', 'Skip')}
      </UIButton>

      <div className="text-center space-y-2 pt-4">
        <h2 className="text-2xl font-semibold">
          {t(
            'generation:model-experiment.dialog.title',
            'Which describes these two results best?',
          )}
        </h2>
        <p className="text-sm text-neutral-600">
          {t(
            'generation:model-experiment.dialog.subtitle',
            'Both images were generated from the same input. Please select the one that better matches your intent.',
          )}
        </p>
      </div>

      <div className="flex gap-4 justify-center flex-1 min-h-0">
        <ImageSlot
          chipLabel={t('generation:model-experiment.dialog.left-chip', 'Left')}
          mediaUrl={leftMedia.mediaUrl}
          mediaId={leftMedia.mediaId}
        />
        <ImageSlot
          chipLabel={t(
            'generation:model-experiment.dialog.right-chip',
            'Right',
          )}
          mediaUrl={rightMedia.mediaUrl}
          mediaId={rightMedia.mediaId}
        />
      </div>

      {/* 1fr|auto|1fr — left/right buttons share equal width while
          "About the same" hugs its own content, visually subordinate
          to the two primary choices. */}
      <div className="grid gap-3 items-stretch grid-cols-[1fr_auto_1fr]">
        <UIButton
          variant="outlined"
          size="large"
          icon={<IcBaselineArrowBack />}
          className="justify-start font-medium"
          onPress={() => onChoose('picked_left')}
        >
          {t(
            'generation:model-experiment.dialog.choice-left-better',
            'Left better',
          )}
        </UIButton>
        <UIButton
          variant="ghost"
          size="large"
          className="text-neutral-600 font-normal"
          onPress={() => onChoose('tie')}
        >
          {t('generation:model-experiment.dialog.choice-tie', 'About the same')}
        </UIButton>
        <UIButton
          variant="outlined"
          size="large"
          endIcon={<IcBaselineArrowForward />}
          className="justify-end font-medium"
          onPress={() => onChoose('picked_right')}
        >
          {t(
            'generation:model-experiment.dialog.choice-right-better',
            'Right better',
          )}
        </UIButton>
      </div>
    </div>
  )
}

function ImageSlot(props: {
  chipLabel: string
  mediaUrl: string | null
  mediaId: string
}) {
  return (
    <div
      // width-driven via `flex-1 min-w-0`, with `max-h-full` clamping the
      // aspect-derived height to the available row height — so on tight
      // viewports the slot shrinks instead of forcing the dialog past
      // `max-w-[90vw]`.
      className="relative bg-neutral-100 rounded-lg overflow-hidden aspect-square flex-1 min-w-0 max-h-full"
    >
      <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium text-white bg-neutral-900/80 rounded">
        {props.chipLabel}
      </span>
      {props.mediaUrl ? (
        <img
          src={props.mediaUrl}
          alt={props.mediaId}
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="w-full h-full bg-neutral-200" />
      )}
    </div>
  )
}

function DialogSkeleton() {
  // Mirrors the real layout so the size doesn't snap when the skeleton
  // swaps to content: 55vh tall, 90vw wide cap, image slots are
  // `flex-1 min-w-0 max-h-full aspect-square`.
  return (
    <div className="flex flex-col gap-6 p-8 h-[55vh] max-w-[90vw]">
      <div className="h-8 w-2/3 mx-auto bg-neutral-200 rounded animate-pulse" />
      <div className="flex gap-4 justify-center flex-1 min-h-0">
        <div className="flex-1 min-w-0 max-h-full aspect-square bg-neutral-200 rounded animate-pulse" />
        <div className="flex-1 min-w-0 max-h-full aspect-square bg-neutral-200 rounded animate-pulse" />
      </div>
    </div>
  )
}

// =============================================================================
// Mobile fallback — a dismissable notice that redirects users to desktop.
// The two-image comparison doesn't fit well on a phone screen and the
// preference data quality suffers when the user can't see both images at
// native size. Exported for tests; in production it's only invoked via
// `openModelExperimentDialog` when `getIsMobile()` is true.
// =============================================================================

export function MobileFallbackContent({ close }: { close: () => void }) {
  const { t } = useTranslation(['generation'])
  return (
    <div className="flex flex-col gap-4 p-6 max-w-sm text-center">
      <h2 className="text-lg font-semibold">
        {t(
          'generation:model-experiment.mobile-fallback.title',
          'Open on desktop to choose',
        )}
      </h2>
      <p className="text-sm text-neutral-600">
        {t(
          'generation:model-experiment.mobile-fallback.body',
          'This A/B image comparison works best on a wider screen. Please open PixAI on a desktop to pick between your two generated images.',
        )}
      </p>
      <UIButton
        variant="filled"
        size="normal"
        className="self-center px-6"
        onPress={close}
      >
        {t('generation:model-experiment.mobile-fallback.dismiss', 'Got it')}
      </UIButton>
    </div>
  )
}

// =============================================================================
// Notice dialog — passive explanation that the experiment session moved on
// without this user (cross-device pick, expiry, etc). Dismissable.
// =============================================================================

export function openModelExperimentNoticeDialog(kind: NoticeKind) {
  return UIDialog.create({
    dismissable: true,
    type: 'dialog',
    children: ({ close }: { close: () => void }) => (
      <NoticeDialogContent kind={kind} close={close} />
    ),
  })
}

export function NoticeDialogContent({
  kind,
  close,
}: {
  kind: NoticeKind
  close: () => void
}) {
  const { t } = useTranslation(['generation'])
  const message =
    kind === 'already-resolved'
      ? t(
          'generation:model-experiment.dialog.already-resolved',
          'This choice was already made on another device.',
        )
      : t(
          'generation:model-experiment.dialog.expired',
          'This experiment session has ended.',
        )
  return (
    <div className="flex flex-col gap-4 p-8 max-w-md text-center">
      <p>{message}</p>
      <UIButton
        variant="filled"
        size="normal"
        className="self-center px-6"
        onPress={close}
      >
        <Text i18nKey="action:ok" />
      </UIButton>
    </div>
  )
}
